import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from "react";
/**
 * Question headers are unique within a single ask_questions call, so
 * `toolCallId::header` is unique. Uses a printable separator so the value is
 * also valid as a radio-group `name` attribute.
 */
function fieldKey(toolCallId, header) {
    return `${toolCallId}::${header}`;
}
/** Build the combined answer string for one question from its field state. */
function composeAnswer(state) {
    if (!state)
        return "";
    const parts = [];
    if (state.selected.length > 0)
        parts.push(state.selected.join(", "));
    const free = state.freeText.trim();
    if (free)
        parts.push(free);
    return parts.join(" — ");
}
/**
 * Shared state + helpers for rendering and collecting answers to one or more
 * ask_questions requests. Used by both the standalone QuestionsForm and the
 * combined PendingResolution panel.
 */
export function useQuestionFields(requests) {
    const [fields, setFields] = useState(() => {
        // Pre-select recommended options as sensible defaults.
        const init = {};
        for (const req of requests) {
            for (const q of req.questions) {
                const recommended = (q.options ?? [])
                    .filter((o) => o.recommended)
                    .map((o) => o.label);
                init[fieldKey(req.toolCallId, q.header)] = {
                    selected: q.multiSelect ? recommended : recommended.slice(0, 1),
                    freeText: "",
                };
            }
        }
        return init;
    });
    const allQuestions = useMemo(() => requests.flatMap((req) => req.questions.map((q) => ({ toolCallId: req.toolCallId, q }))), [requests]);
    const setSelected = (key, label, multi) => {
        setFields((prev) => {
            const cur = prev[key] ?? { selected: [], freeText: "" };
            let selected;
            if (multi) {
                selected = cur.selected.includes(label)
                    ? cur.selected.filter((l) => l !== label)
                    : [...cur.selected, label];
            }
            else {
                selected = cur.selected[0] === label ? [] : [label];
            }
            return { ...prev, [key]: { ...cur, selected } };
        });
    };
    const setFreeText = (key, value) => {
        setFields((prev) => {
            const cur = prev[key] ?? { selected: [], freeText: "" };
            return { ...prev, [key]: { ...cur, freeText: value } };
        });
    };
    const allAnswered = allQuestions.every(({ toolCallId, q }) => composeAnswer(fields[fieldKey(toolCallId, q.header)]).length > 0);
    const buildAnswers = () => {
        const answers = {};
        for (const req of requests) {
            answers[req.toolCallId] = {
                responses: req.questions.map((q) => ({
                    header: q.header,
                    question: q.question,
                    answer: composeAnswer(fields[fieldKey(req.toolCallId, q.header)]),
                })),
            };
        }
        return answers;
    };
    return { fields, setSelected, setFreeText, allAnswered, buildAnswers };
}
/** Renders the question controls (radio/checkbox/free-text) for one or more requests. */
export function QuestionFields({ requests, fields, setSelected, setFreeText, disabled, onEnter, }) {
    return (_jsx(_Fragment, { children: requests.map((req) => req.questions.map((q) => {
            const key = fieldKey(req.toolCallId, q.header);
            const state = fields[key];
            const hasOptions = (q.options?.length ?? 0) > 0;
            return (_jsxs("div", { className: "question-item", children: [_jsx("div", { className: "question-text", children: q.question }), q.message && _jsx("div", { className: "question-message", children: q.message }), hasOptions && (_jsx("div", { className: "question-options", children: q.options.map((opt) => {
                            const checked = state?.selected.includes(opt.label) ?? false;
                            return (_jsxs("label", { className: `question-option${checked ? " is-selected" : ""}`, children: [_jsx("input", { type: q.multiSelect ? "checkbox" : "radio", name: key, checked: checked, disabled: disabled, onChange: () => setSelected(key, opt.label, !!q.multiSelect) }), _jsxs("span", { className: "question-option-label", children: [opt.label, opt.recommended && (_jsx("span", { className: "question-option-badge", children: "recommended" })), opt.description && (_jsx("span", { className: "question-option-desc", children: opt.description }))] })] }, opt.label));
                        }) })), _jsx("input", { type: "text", className: "question-freetext", placeholder: hasOptions ? "Or type your own answer…" : "Type your answer…", value: state?.freeText ?? "", disabled: disabled, onChange: (e) => setFreeText(key, e.target.value), onKeyDown: (e) => {
                            if (e.key === "Enter")
                                onEnter?.();
                        } })] }, key));
        })) }));
}
//# sourceMappingURL=QuestionFields.js.map